import * as yup from 'yup';
import { Clock, EyeOff, Tag } from 'react-feather';
import { Fragment, useState } from 'react';
import { GetInvitationQuery as GetInvitation, ModelType } from '__generated__/graphql';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { TranslatableEditor } from '../../../Translations/Inputs';
import { editorOptions } from '../../../../common/Tiptap/helpers';
import { getServerErrors } from '../../../../common/helpers';
import { replacementKeys } from '../Settings/EditEventRegistrationForm';
import { translationsForAttribute } from '../../../Translations/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import DateRangeForm, { dateRangeObject } from '../../../Common/DateRangeForm';
import EditInvitationMutation from './EditInvitationMutation';
import FindOrAddTeamForm, { findOrAddTeamSchema } from '../../../Participants/Team/FindOrAddTeamForm';
import MailEditorLayout from '../../../Common/MailEditorLayout';
import UI from '../../../../common/UI';
import useIntegerInput from '../../../../common/useIntegerInput';
import useMutation from '../../../../api/useMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useScroll from '../../../../common/useScroll';
import useTimeSlotFormatter from '../../../../common/Tickets/useTimeSlotFormatter';
import useValidation from '../../../../common/useValidation';

type Event = GetInvitation['event'];
type Ticket = GetInvitation['event']['tickets'][0];
type Invitation = GetInvitation['invitation'];

const invitationFormSchema = yup.object().shape({
  prefix: yup.string().required(),
  max_uses: yup.number().max(5000).nullable().when(['unique_codes'], {
    is: (value: boolean) => value,
    then: yup.number().required(),
  }),
  ...dateRangeObject(false, false),
  team: findOrAddTeamSchema.nullable(),
});

export interface EditInvitationFormProps {
  invitation: Invitation;
  event: Event;
}

const EditInvitationForm = ({ invitation, event }: EditInvitationFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();
  const integerInput = useIntegerInput();
  const formatTimeSlot = useTimeSlotFormatter();
  const { scrollToIfInvisible } = useScroll();

  const getValues = (invitation: Invitation) => ({
    id: invitation.id,
    prefix: invitation.prefix,
    unique_codes: invitation.has_unique_codes,
    max_uses: invitation.max_uses,
    ignore_capacity: invitation.ignore_capacity,
    start_date: invitation.start_date,
    end_date: invitation.end_date,
    tickets: invitation.tickets.map(({ id }) => ({ id })),
    promotions: invitation.promotions.map(({ id }) => ({ id })),
    time_slots: invitation.time_slots.map(({ id }) => ({ id })),
    description: invitation.description,
    description_translations: translationsForAttribute(invitation.translations, 'description'),
    confirmation_mail_text: invitation.confirmation_mail_text,
    confirmation_mail_text_translations: translationsForAttribute(invitation.translations, 'confirmation_mail_text'),
    team: invitation.team ? { id: invitation.team.id } : null,
  });

  const form = useForm({
    values: getValues(invitation),
  });

  useNavigationPrompt(form.changed());

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editInvitation, { error, loading }] = useMutation(EditInvitationMutation, {
    variables: {
      input: form.values,
    },
    onCompleted: ({ editInvitation: invitation }) => {
      notifier.success(t('notifications.invitation_saved'));
      form.reset(getValues(invitation));
      setFormKey((key) => key + 1);
    },
    onError: () => {
      scrollToIfInvisible(`InvitationCard_${invitation.id}`);
    },
  });

  const { valid, errors } = useValidation({
    schema: invitationFormSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const deselectTickets = () => {
    form.set('tickets', []);
    form.set('promotions', []);
    form.set('time_slots', []);
  };

  const deselectPromotions = (ticket: Ticket) => {
    const promotionIds = ticket.promotions.map(({ id }) => id);

    form.set('promotions', (values: ModelType[]) => (
      values.filter(({ id }) => !promotionIds.includes(id))
    ));
  };

  const hasSelectedPromotions = (ticket: Ticket) => {
    const promotionIds = ticket.promotions.map(({ id }) => id);

    return form.values.promotions.filter(({ id }) => promotionIds.includes(id)).length > 0;
  };

  const deselectTimeSlots = (ticket: Ticket) => {
    const timeSlotIds = ticket.time_slots.map(({ id }) => id);

    form.set('time_slots', (values: ModelType[]) => (
      values.filter(({ id }) => !timeSlotIds.includes(id))
    ));
  };

  const hasSelectedTimeSlots = (ticket: Ticket) => {
    const timeSlotIds = ticket.time_slots.map(({ id }) => id);

    return form.values.time_slots.filter(({ id }) => timeSlotIds.includes(id)).length > 0;
  };

  const isSelected = (type: 'tickets' | 'time_slots' | 'promotions', model: ModelType) => (
    form.values[type].filter(({ id }) => id === model.id).length > 0
  );

  return (
    <UI.Div id={`EditInvitationForm_${invitation.id}`} key={formKey}>
      <UI.Form onSubmit={() => editInvitation()}>
        <UI.FormGrid>
          <UI.ServerErrors error={error} />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('basic_info')}
            </UI.Legend>
            <UI.InputDescription>
              {t('invitation_basic_info')}
            </UI.InputDescription>
          </UI.Div>

          <UI.InputGroup sc={{ valid: !errors.prefix, touched: form.touched.prefix }}>
            <UI.InputLabel htmlFor="InvitationPrefix">
              {t('invitation_code')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('prefix')}
              placeholder={t('invitation_code_placeholder')}
              id="InvitationPrefix"
            />
            <UI.ErrorMessages attribute={t('invitation_code')} errors={errors.prefix} />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.unique_codes, touched: form.touched.unique_codes }}>
            <UI.InputLabel>
              {t('invitation_type')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.FormGrid sc={{ gutter: 0.25 }}>
              <UI.Radio
                onChange={() => form.set('unique_codes', false)}
                checked={!form.values.unique_codes}
                sc={{ box: true }}
              >
                <UI.Strong sc={{ block: true }}>
                  {t('general')}
                </UI.Strong>
                {t('invitation_type_general_description')}
              </UI.Radio>
              <UI.Radio
                onChange={() => form.set('unique_codes', true)}
                checked={form.values.unique_codes}
                sc={{ box: true }}
              >
                <UI.Strong sc={{ block: true }}>
                  {t('personal')}
                </UI.Strong>
                {t('invitation_type_personal_description')}
              </UI.Radio>
            </UI.FormGrid>
            <UI.ErrorMessages
              attribute={t('invitation_type')}
              errors={errors.unique_codes}
            />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.max_uses, touched: form.touched.max_uses }}>
            <UI.InputLabel htmlFor="InvitationMaxUses">
              {t('number_of_reserved_tickets')}
              {form.values.unique_codes && (
                <>
                  {' '}
                  <UI.RequiredMark />
                </>
              )}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('invitation_max_uses_description')}
            </UI.InputDescription>
            <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
              <UI.Div>
                <UI.DebouncedInput
                  {...form.text('max_uses', { inputHandler: integerInput() })}
                  maxLength={4}
                  inputMode="numeric"
                  format={integerInput().format}
                  match={/[\d]/g}
                  id="InvitationMaxUses"
                />
              </UI.Div>
            </UI.FormGrid>
            <UI.ErrorMessages attribute={t('max_uses')} errors={errors.max_uses} />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.ignore_capacity, touched: form.touched.ignore_capacity }}>
            <UI.InputLabel>
              {t('ticket_capacity')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('invitation_ignore_capacity_description')}
            </UI.InputDescription>
            <UI.Checkbox
              onChange={() => form.set((values) => ({ ...values, ignore_capacity: !values.ignore_capacity }))}
              checked={form.values.ignore_capacity}
            >
              {t('ignore_ticket_capacity')}
            </UI.Checkbox>
            <UI.ErrorMessages
              attribute={t('ticket_capacity')}
              errors={errors.ignore_capacity}
            />
          </UI.InputGroup>

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('validity')}
            </UI.Legend>
            <UI.InputDescription>
              {t('invitation_tickets_and_time_slots_description')}
            </UI.InputDescription>
          </UI.Div>

          <UI.Info>
            {t('invitation_special_rate_description')}
          </UI.Info>

          <UI.InputGroup sc={{ valid: !errors.tickets, touched: form.touched.tickets }}>
            <UI.InputLabel>
              {t('tickets')}
            </UI.InputLabel>
            <UI.Checkbox
              checked={form.values.tickets.length === 0}
              onChange={() => deselectTickets()}
            >
              <UI.Span sc={{ muted: form.values.tickets.length !== 0 }}>
                {t('all_active_tickets')}
              </UI.Span>
            </UI.Checkbox>
            {event.tickets.map((ticket) => (
              <Fragment key={ticket.id}>
                <UI.Checkbox
                  {...form.checklist('tickets', { id: ticket.id }, {
                    key: 'id',
                    onChange: (values: ModelType[]) => {
                      if (!values.map(({ id }) => id).includes(ticket.id)) {
                        deselectPromotions(ticket);
                        deselectTimeSlots(ticket);
                      }
                    },
                  })}
                  key={ticket.id}
                >
                  <UI.Span sc={{ muted: (!ticket.active || ticket.preregistration) && !isSelected('tickets', ticket) }}>
                    {ticket.title}
                  </UI.Span>
                  {!ticket.active && (
                    <>
                      {' '}
                      <UI.TooltipContainer sc={{ inlineBlock: true }}>
                        <UI.Icon sc={{ muted: true }}>
                          <EyeOff />
                        </UI.Icon>
                        <UI.Tooltip sc={{ alignH: 'center' }}>
                          {t('invitation_can_unlock_inactive_tickets')}
                        </UI.Tooltip>
                      </UI.TooltipContainer>
                    </>
                  )}
                  {ticket.preregistration && (
                    <>
                      {' '}
                      <UI.TooltipContainer sc={{ inlineBlock: true }}>
                        <UI.Icon sc={{ muted: true }}>
                          <UI.Icons.Pending />
                        </UI.Icon>
                        <UI.Tooltip sc={{ alignH: 'center' }}>
                          {t('invitation_can_access_preregistration')}
                        </UI.Tooltip>
                      </UI.TooltipContainer>
                    </>
                  )}
                </UI.Checkbox>
                {form.values.tickets.map(({ id }) => id).includes(ticket.id) && ticket.promotions.length > 1 && (
                  <UI.Div sc={{ pl: 4 }}>
                    <UI.Checkbox
                      checked={!hasSelectedPromotions(ticket)}
                      onChange={() => deselectPromotions(ticket)}
                    >
                      <UI.Span sc={{ muted: hasSelectedPromotions(ticket) }}>
                        {t('all_active_rates')}
                      </UI.Span>
                    </UI.Checkbox>
                    {ticket.promotions.map((promotion) => (
                      <UI.Checkbox
                        {...form.checklist('promotions', { id: promotion.id }, { key: 'id' })}
                        key={promotion.id}
                      >
                        <UI.Icon sc={{ muted: true }}>
                          <Tag />
                        </UI.Icon>
                        {' '}
                        <UI.Span sc={{ muted: !promotion.active && !isSelected('promotions', promotion) }}>
                          {promotion.title || t('standard')}
                        </UI.Span>
                        {!promotion.active && (
                          <>
                            {' '}
                            <UI.TooltipContainer sc={{ inlineBlock: true }}>
                              <UI.Icon sc={{ muted: true }}>
                                <EyeOff />
                              </UI.Icon>
                              <UI.Tooltip sc={{ alignH: 'center' }}>
                                {t('invitation_can_unlock_inactive_rates')}
                              </UI.Tooltip>
                            </UI.TooltipContainer>
                          </>
                        )}
                      </UI.Checkbox>
                    ))}
                  </UI.Div>
                )}
                {form.values.tickets.map(({ id }) => id).includes(ticket.id) && ticket.time_slots.length > 0 && (
                  <UI.Div sc={{ pl: 4 }}>
                    <UI.Checkbox
                      checked={!hasSelectedTimeSlots(ticket)}
                      onChange={() => deselectTimeSlots(ticket)}
                    >
                      <UI.Span sc={{ muted: hasSelectedTimeSlots(ticket) }}>
                        {t('all_active_time_slots')}
                      </UI.Span>
                    </UI.Checkbox>
                    {ticket.time_slots.map((timeSlot) => (
                      <UI.Checkbox
                        {...form.checklist('time_slots', { id: timeSlot.id }, { key: 'id' })}
                        key={timeSlot.id}
                      >
                        <UI.Icon sc={{ muted: true }}>
                          <Clock />
                        </UI.Icon>
                        {' '}
                        <UI.Span sc={{ muted: !timeSlot.active && !isSelected('time_slots', timeSlot) }}>
                          {formatTimeSlot(timeSlot)}
                          {timeSlot.title && ` • ${timeSlot.title}`}
                        </UI.Span>
                        {!timeSlot.active && (
                          <>
                            {' '}
                            <UI.TooltipContainer sc={{ inlineBlock: true }}>
                              <UI.Icon sc={{ muted: true }}>
                                <EyeOff />
                              </UI.Icon>
                              <UI.Tooltip sc={{ alignH: 'center' }}>
                                {t('invitation_can_unlock_inactive_time_slots')}
                              </UI.Tooltip>
                            </UI.TooltipContainer>
                          </>
                        )}
                      </UI.Checkbox>
                    ))}
                  </UI.Div>
                )}
              </Fragment>
            ))}
            <UI.ErrorMessages attribute={t('tickets')} errors={errors.tickets} />
            <UI.ErrorMessages attribute={t('promotions')} errors={errors.promotions} />
            <UI.ErrorMessages attribute={t('time_slots')} errors={errors.time_slots} />
          </UI.InputGroup>

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('availability')}
            </UI.Legend>
            <UI.InputDescription>
              {t('invitation_start_and_end_date')}
            </UI.InputDescription>
          </UI.Div>

          <UI.Div style={{ zIndex: 11 /* Make sure the datepicker dropdown doesn't go behind the TeamPicker */ }}>
            <DateRangeForm form={form} errors={errors} />
          </UI.Div>

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('descriptions')}
            </UI.Legend>
            <UI.InputDescription>
              {t('invitation_descriptions')}
            </UI.InputDescription>
          </UI.Div>

          <UI.InputGroup
            sc={{ valid: !errors.description, touched: form.touched.description }}
          >
            <UI.InputLabel id="InvitationDescriptionLabel">
              {t('description')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('invitation_description_description')}
            </UI.InputDescription>
            <TranslatableEditor
              form={form}
              errors={errors}
              name="description"
              defaultLocale={event.locale}
              title={t('description')}
              attributes={{
                'aria-labelledby': 'InvitationDescriptionLabel',
              }}
            />
            <UI.ErrorMessages
              attribute={t('description')}
              errors={errors.description}
            />
          </UI.InputGroup>

          <UI.InputGroup
            sc={{ valid: !errors.confirmation_mail_text, touched: form.touched.confirmation_mail_text }}
          >
            <UI.InputLabel id="InvitationConfirmationMailLabel">
              {t('confirmation_mail')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('invitation_confirmation_mail_description')}
            </UI.InputDescription>
            <MailEditorLayout
              editor={(props) => (
                <TranslatableEditor
                  form={form}
                  errors={errors}
                  name="confirmation_mail_text"
                  defaultLocale={event.locale}
                  title={t('confirmation_mail')}
                  variables={replacementKeys}
                  options={editorOptions.full}
                  attributes={{
                    'aria-labelledby': 'InvitationConfirmationMailLabel',
                  }}
                  {...props}
                />
              )}
              value={form.values.confirmation_mail_text}
              event={event}
              showRegistrationSummary
              showOrderSummary
            />
            <UI.ErrorMessages
              attribute={t('confirmation_mail')}
              errors={errors.confirmation_mail_text}
            />
          </UI.InputGroup>

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('team')}
            </UI.Legend>
            <UI.InputDescription>
              {t('invitation_team_description')}
            </UI.InputDescription>
          </UI.Div>

          <FindOrAddTeamForm
            form={form}
            eventId={event.id}
            team={invitation.team}
            errors={errors}
          />

          {event.private_teams && (
            <UI.Info>
              {t('private_teams_invitation_description')}
            </UI.Info>
          )}

          <UI.HR />

          <UI.FormButtons
            form={form}
            submitLabel={t('save_invitation')}
            closeTo={`/${project.id}/events/${event.id}/invitation-codes`}
            loading={loading}
            valid={valid}
          />
        </UI.FormGrid>
      </UI.Form>
    </UI.Div>
  );
};

export default EditInvitationForm;
